import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { postAPI, reinitialiseAPI } from 'store/apiReducer';
import { Button, Row, Col, Container, ButtonGroup, ButtonLink } from '@si-armes/dsfr-react';
import usagerClientReducer from 'store/usagerReducer';
import {
    API_CHANGER_MOT_DE_PASSE,
    CONTEXT_API_CHANGER_MOT_DE_PASSE,
} from 'constants/apiUrls.constants';
import {
    isPswdIdentique,
    isPswdValid,
} from '../../../compte/creer/etape-2/components/MotDePasseCreationCompteEtape2';
import InputPassword from '../../../../components/Input/InputPassword';

const ChangerMotDePasse = ({ postApi, reinitialiseApi, isLoading, erreur, message }) => {
    const history = useHistory();
    const { register, handleSubmit, watch, setValue } = useForm({
        mode: 'onChange',
        criteriaMode: 'all',
    });

    const URL_ECRAN_GESTION = '/espace-detenteurs/mon-compte/gestion';

    const hasFormValid = () => {
        return (
            watch('ancienMotDePasse') &&
            isPswdValid(watch('motDePasse')) &&
            isPswdIdentique(watch('motDePasse'), watch('confirmationMotDePasse')) &&
            !isPswdIdentique(watch('ancienMotDePasse'), watch('motDePasse'))
        );
    };

    const onSubmit = (dataForm) => {
        if (dataForm && hasFormValid()) {
            const dataFormate = {
                ancienMotDePasse: dataForm.ancienMotDePasse,
                motDePasse: dataForm.motDePasse,
            };

            postApi(
                usagerClientReducer,
                API_CHANGER_MOT_DE_PASSE,
                dataFormate,
                CONTEXT_API_CHANGER_MOT_DE_PASSE,
            );
        }
    };

    useEffect(() => {
        reinitialiseApi(CONTEXT_API_CHANGER_MOT_DE_PASSE);

        return () => {
            reinitialiseApi(CONTEXT_API_CHANGER_MOT_DE_PASSE);
        };
    }, []);

    useEffect(() => {
        if (!isLoading && !erreur && message) {
            setValue('ancienMotDePasse', null);
            setValue('motDePasse', null);
            setValue('confirmationMotDePasse', null);
            history.push(URL_ECRAN_GESTION);
        }
    }, [isLoading, erreur, message]);
    const getErreurMotDePasseInvalide = () => {
        if (watch('motDePasse') && !isPswdValid(watch('motDePasse'))) {
            return {
                message:
                    'Le mot de passe renseigné ne contient pas tous les éléments obligatoires. Veuillez en saisir un respectant le format indiqué.',
            };
        }
        if (
            watch('ancienMotDePasse') &&
            watch('motDePasse') &&
            isPswdIdentique(watch('ancienMotDePasse'), watch('motDePasse'))
        ) {
            return {
                message:
                    'Le mot de passe renseigné ne contient pas tous les éléments obligatoires ou est identique à votre mot de passe actuel. Veuillez saisir un mot de passe respectant les règles indiquées.',
            };
        }

        return null;
    };

    const getErreurMotDePasseNonIdentique = () => {
        if (
            watch('confirmationMotDePasse') &&
            !isPswdIdentique(watch('motDePasse'), watch('confirmationMotDePasse'))
        ) {
            return {
                message: "Veuillez renseigner le même mot de passe qu'au champ précédent.",
            };
        }
        return null;
    };

    return (
        <Container>
            <Row>
                <Col col={12}>
                    <h1>Changement de mot de passe</h1>
                </Col>
            </Row>
            <Row>
                <Col col={12}>
                    <div className="gras">
                        <p>
                            Veuillez renseigner votre nouveau mot de passe en respectant les règles
                            énoncées ci-dessous.
                        </p>
                        <p>
                            Le nouveau mot de passe doit être différent de votre mot de passe
                            actuel.
                        </p>
                    </div>
                </Col>
            </Row>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row gutters style={{ 'flex-direction': 'row-reverse' }}>
                    {/*
                    Le row reverse est là pour répondre à une contrainte d'accessibilité :
                    Les indications de mot de passe doivent être avant le champ afin que l'utilisateur connaisse
                    les contraintes.
                    Les indications ont donc été placé avant pour que les lecteur d'écrans commencent par ça, mais
                    pour conserver l'affichage, on a utilisé un row-reverse afin qu'elles s'affichent à droite.
                    */}
                    <Col col={12} lg={6}>
                        <p> Le mot de passe doit contenir: </p>
                        <ul>
                            <li>au moins 1 Majuscule</li>
                            <li>au moins 1 Minuscule</li>
                            <li>au moins 1 Chiffre</li>
                            <li>
                                au moins 1 Caractère spécial
                                <br />
                                (ex : + , - , ? , ! , % , etc.)
                            </li>
                            <li>12 caractères minimum</li>
                        </ul>
                    </Col>
                    <Col col={12} lg={6}>
                        <InputPassword
                            name="ancienMotDePasse"
                            label="Veuillez saisir votre mot de passe actuel"
                            maxLength="300"
                            {...register('ancienMotDePasse', {
                                required: true,
                            })}
                            required
                        />
                        <InputPassword
                            name="motDePasse"
                            label="Veuillez saisir un nouveau mot de passe"
                            maxLength="300"
                            {...register('motDePasse', {
                                required: true,
                            })}
                            error={getErreurMotDePasseInvalide()}
                            required
                        />

                        <InputPassword
                            name="confirmationMotDePasse"
                            label="Confirmez votre nouveau mot de passe"
                            maxLength="300"
                            {...register('confirmationMotDePasse', {
                                required: true,
                            })}
                            error={getErreurMotDePasseNonIdentique()}
                            required
                        />
                    </Col>
                </Row>
                <Row className="margin-top-lg">
                    <Col col={12}>
                        <ButtonGroup inline align="right">
                            <ButtonLink
                                as={Link}
                                to={URL_ECRAN_GESTION}
                                variant="secondary"
                                aria-label="Annuler et revenir à la gestion du profil"
                            >
                                Annuler
                            </ButtonLink>
                            <Button
                                name="soumettre"
                                variant="primary"
                                type="submit"
                                disabled={!hasFormValid()}
                                isLoading={isLoading}
                            >
                                Soumettre
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </form>
        </Container>
    );
};

ChangerMotDePasse.propTypes = {
    postApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    erreur: PropTypes.bool,
    message: PropTypes.string,
};

export default connect(
    (state) => {
        const { isLoading, erreur, message } = state.monCompte.changerMotDePasse;
        return {
            isLoading,
            erreur,
            message,
        };
    },
    {
        postApi: postAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(ChangerMotDePasse);
